import React from 'react';
import { Button, RenderIf } from '@lib/ui-components';
import { LoadingIndicator } from './loading-indicator';
import { FetchErrorIndicator } from './fetch-error-indicator';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export interface ExtractedItems<T> {
  data: T;
  currentPage: number;
  hasNextPage: boolean;
  totalItems: number;
}

export interface InfiniteScrollRendererProps<RowType> {
  query: UseInfiniteQueryResult<InfiniteData<ExtractedItems<RowType[]>, unknown>>;
  rowRenderer: (row: RowType) => JSX.Element;
  headerRenderer?: (rows: RowType[]) => JSX.Element;
}

export const InfiniteScrollRenderer = <T,>({ query, rowRenderer, headerRenderer }: InfiniteScrollRendererProps<T>) => {
  const { t } = useTranslation();

  return (
    <>
      {/*
        `?? []` rather than a bare `value.data`: a page whose body does not carry `data` used to flatten to
        [undefined] and hand rowRenderer an undefined row, which took the whole screen down with a stack trace.
        The api and the web app deploy as two images, so a request answered by the previous api version is a
        normal state for a minute during every release - it should render an empty list, not a crash.
      */}
      {headerRenderer?.(query.data?.pages.flatMap((value) => value.data ?? []) || [])}
      {query.data?.pages
        ?.flatMap((value) => value.data ?? [])
        ?.map((row) => {
          return rowRenderer(row);
        })}
      <RenderIf true={query.isFetching}>
        <LoadingIndicator />
      </RenderIf>
      <RenderIf true={query.error != null}>
        <FetchErrorIndicator error={query.error} />
      </RenderIf>
      <RenderIf true={query.data?.pages[query.data.pages.length - 1]?.hasNextPage}>
        <Button onClick={() => query.fetchNextPage()} label={t('more')} />
      </RenderIf>
    </>
  );
};
